import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { alpha, lighten, useTheme } from "@material-ui/core/styles";
import {
  makeStyles,
  Paper,
  TextField,
  Button,
  IconButton,
  Checkbox,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  Collapse,
  Snackbar,
  useMediaQuery,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PersonIcon from "@material-ui/icons/Person";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import CloseIcon from "@material-ui/icons/Close";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AlarmIcon from "@material-ui/icons/Alarm";
import SubjectIcon from "@material-ui/icons/Subject";
import Menu from "@material-ui/core/Menu";
import { toast } from "react-toastify";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import TaskEditor from "../../components/TaskEditor";
import NewTicketModal from "../../components/NewTicketModal";
import { postponeOptions } from "../../components/TaskEditor/taskDates";
import {
  taskDaysUntil,
  isTaskOverdue,
  taskDueInput,
  taskDueTime,
  taskDueSortKey,
  formatTaskDue,
  buildDuePayload,
} from "../../helpers/taskDue";
import { AuthContext } from "../../context/Auth/AuthContext";

const PRIORITY_LABELS = { high: "Alta", medium: "Média", low: "Baixa" };
const PRIORITY_COLORS = { high: "#e53935", medium: "#fb8c00", low: "#1e88e5" };
const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
const DONE_PAGE_SIZE = 30;
// conclusão: check fica visível um instante, depois o card recolhe
const CHECK_PAUSE_MS = 450;
const COLLAPSE_MS = 300;
const UNDO_MS = 6000;

// leitura do prazo (dia todo x horário, formatos antigos): ver helpers/taskDue
const daysUntilDue = task => taskDaysUntil(task);
const isOverdue = task => isTaskOverdue(task);

const GROUPS = [
  { key: "overdue", label: "Atrasadas", match: d => d !== null && d < 0 },
  { key: "today", label: "Hoje", match: d => d === 0 },
  { key: "week", label: "Próximos 7 dias", match: d => d !== null && d > 0 && d <= 7 },
  { key: "later", label: "Mais tarde", match: d => d !== null && d > 7 },
  { key: "nodate", label: "Sem prazo", match: d => d === null },
];

const SORTERS = {
  priority: (a, b) =>
    (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1) ||
    taskDueSortKey(a) - taskDueSortKey(b),
  dueDate: (a, b) =>
    taskDueSortKey(a) - taskDueSortKey(b) ||
    (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1),
  recent: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
};

const TABS = [
  { key: "pending", label: "Pendentes" },
  { key: "overdue", label: "Atrasadas" },
  { key: "done", label: "Concluídas" },
];

const matchesSearch = (task, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    task.text?.toLowerCase().includes(t) ||
    task.contact?.name?.toLowerCase().includes(t) ||
    task.opportunity?.title?.toLowerCase().includes(t)
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    // o CssBaseline do app fica fora do ThemeProvider: sem isso o texto
    // herda o preto do tema claro no tema escuro
    color: theme.palette.text.primary,
    padding: theme.spacing(3),
    maxWidth: 860,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  header: {
    marginBottom: theme.spacing(2.5),
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  summary: {
    marginTop: 4,
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
    borderRadius: 14,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
  },
  formRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: { alignItems: "stretch", flexDirection: "column" },
  },
  priorityPicker: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  priorityOption: {
    height: 32,
    padding: theme.spacing(0, 1.5),
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.primary,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    font: "500 13px Inter, Roboto, sans-serif",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
  },
  priorityDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  formDate: {
    width: 180,
    [theme.breakpoints.down("xs")]: { width: "100%" },
  },
  addButton: {
    marginLeft: "auto",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2),
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: { marginLeft: 0 },
  },
  textButton: {
    textTransform: "none",
    fontWeight: 600,
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  scopeToggle: {
    display: "flex",
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  scopeButton: {
    height: 30,
    padding: theme.spacing(0, 1.75),
    border: 0,
    cursor: "pointer",
    background: "transparent",
    color: theme.palette.text.secondary,
    font: "600 13px Inter, Roboto, sans-serif",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  scopeButtonActive: {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
  },
  newTaskButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2),
  },
  owner: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    color: theme.palette.type === "dark" ? "#CFE0FA" : theme.palette.primary.dark,
  },
  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  toolbarRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  search: {
    flex: 1,
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
    },
    [theme.breakpoints.down("xs")]: { flexBasis: "100%" },
  },
  sortSelect: {
    minWidth: 170,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
    },
  },
  tabChip: {
    height: 32,
    borderRadius: 16,
    fontWeight: 500,
  },
  tabs: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(1),
    borderRadius: 10,
    backgroundColor: theme.palette.action.selected,
    "& .MuiButton-root": { textTransform: "none", fontWeight: 600, borderRadius: 8 },
  },
  bulkCount: { flex: 1, fontSize: "0.875rem", fontWeight: 600 },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    userSelect: "none",
    margin: theme.spacing(2.5, 0, 1),
    padding: 0,
    border: 0,
    background: "transparent",
    width: "100%",
    color: theme.palette.text.secondary,
    font: "600 12px Inter, Roboto, sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      marginLeft: 6,
      backgroundColor: theme.palette.divider,
    },
  },
  groupHeaderOverdue: { color: theme.palette.type === "dark" ? "#FF8A80" : "#e53935" },
  groupCount: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    boxSizing: "border-box",
    borderRadius: 999,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: 0,
  },
  "@keyframes checkPop": {
    "0%": { transform: "scale(1)" },
    "40%": { transform: "scale(1.35)" },
    "100%": { transform: "scale(1)" },
  },
  "@keyframes strike": {
    from: { backgroundSize: "0% 2px" },
    to: { backgroundSize: "100% 2px" },
  },
  // espaçamento fica no wrapper (padding) para o Collapse medir a altura certa
  taskSlot: {
    paddingBottom: theme.spacing(1),
  },
  taskCard: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 1.5, 1.5, 1.25),
    transition: "background-color 250ms ease, opacity 250ms ease, border-color 150ms ease",
    borderRadius: 12,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: "3px solid transparent",
    "&:hover": { borderColor: theme.palette.text.disabled },
    "&:hover .todo-actions, &:focus-within .todo-actions": { opacity: 1 },
  },
  taskCardOverdue: {
    borderLeftColor: "#e53935",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(229,57,53,0.08)"
        : "rgba(229,57,53,0.04)",
  },
  taskCardCompleted: {
    opacity: 0.6,
  },
  taskCardCompleting: {
    opacity: 0.7,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(76,175,80,0.12)"
        : "rgba(76,175,80,0.08)",
  },
  checkboxPop: {
    "& svg": { animation: "$checkPop 300ms ease-out" },
    "@media (prefers-reduced-motion: reduce)": {
      "& svg": { animation: "none" },
    },
  },
  // risco desenhado da esquerda para a direita (inclusive em textos com várias linhas)
  taskTextStrike: {
    color: theme.palette.text.disabled,
    transition: "color 250ms ease",
    backgroundImage: "linear-gradient(currentColor, currentColor)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "0 60%",
    backgroundSize: "100% 2px",
    animation: "$strike 300ms ease-out",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  undoButton: {
    color: "#81c784",
    fontWeight: 700,
  },
  undoSnackbar: {
    [theme.breakpoints.down("xs")]: {
      bottom: "calc(8px + env(safe-area-inset-bottom))",
    },
  },
  taskCardSelected: {
    outline: `2px solid ${theme.palette.primary.main}`,
  },
  taskBody: {
    flex: 1,
    minWidth: 0,
    marginLeft: theme.spacing(1),
  },
  taskText: {
    fontSize: 15,
    lineHeight: "21px",
    fontWeight: 500,
    wordBreak: "break-word",
  },
  taskTextCompleted: {
    textDecoration: "line-through",
    color: theme.palette.text.disabled,
  },
  taskMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  priorityBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 22,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
  },
  linkChip: {
    height: 22,
    fontSize: 12,
    maxWidth: 240,
    borderRadius: 999,
  },
  dueDateText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  dueDateOverdue: {
    color: "#e53935",
    fontWeight: 600,
  },
  dueDateToday: {
    color: "#fb8c00",
    fontWeight: 600,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    opacity: 0.55,
    transition: "opacity .12s",
    "@media (hover: none)": { opacity: 1 },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(5, 2),
    color: theme.palette.text.secondary,
    borderRadius: 12,
    border: `1px dashed ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
  checkboxRoot: {
    color: theme.palette.success.main,
    padding: 4,
    "&$checked": { color: theme.palette.success.main },
  },
  checked: {},
  inlineInput: {
    fontSize: "0.95rem",
    width: "100%",
  },
  editRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
  },
  editDate: {
    width: 170,
    [theme.breakpoints.down("xs")]: { flex: 1, width: "auto", minWidth: 150 },
  },
  editButtons: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: { flexBasis: "100%", justifyContent: "flex-end" },
  },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
}));

const dueLabel = task => {
  const days = daysUntilDue(task);
  if (days === null) return null;
  const time = taskDueTime(task);
  const at = time ? ` às ${time}` : "";
  if (task.completed) return `Prazo: ${formatTaskDue(task)}`;
  if (days < 0) return `Atrasada · ${formatTaskDue(task)}`;
  if (days === 0) return isOverdue(task) ? `Atrasada · hoje${at}` : `Vence hoje${at}`;
  if (days === 1) return `Vence amanhã${at}`;
  return `Prazo: ${formatTaskDue(task)}`;
};

const ToDoList = () => {
  const theme = useTheme();
  // no tema escuro as cores de prioridade/atraso ficam mais claras (contraste)
  const tone = c => (theme.palette.type === "dark" ? lighten(c, 0.3) : c);
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const canSeeTeam = ["admin", "supervisor"].includes(user?.profile);
  // "mine" | "team" (team só para admin/supervisor)
  const [scope, setScope] = useState("mine");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const teamView = canSeeTeam && scope === "team";
  // painel de edição / janela de criação / menu "Adiar" / conversa
  const [editorTask, setEditorTask] = useState(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [postpone, setPostpone] = useState(null); // { el, task }
  const [chatContact, setChatContact] = useState(null);

  const [pendingTasks, setPendingTasks] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  const [doneTasks, setDoneTasks] = useState([]);
  const [loadingDone, setLoadingDone] = useState(false);
  const [doneHasMore, setDoneHasMore] = useState(false);

  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("priority");
  const [collapsed, setCollapsed] = useState({});

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ text: "", priority: "medium", dueDate: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [newDueDate, setNewDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  // id -> "checked" (marcada, ainda visível) | "leaving" (recolhendo)
  const [completing, setCompleting] = useState({});
  const [undoTasks, setUndoTasks] = useState([]);
  const leaveTimers = useRef({});
  const undoTimer = useRef(null);
  const restoredIds = useRef(new Set());
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(
    () => () => {
      Object.values(leaveTimers.current).forEach(clearTimeout);
      clearTimeout(undoTimer.current);
    },
    []
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks", {
        params: { status: "pending", scope: teamView ? "team" : undefined },
      });
      setPendingTasks(data);
    } catch {
      toast.error("Erro ao carregar tarefas.");
    } finally {
      setLoadingPending(false);
    }
  }, [teamView]);

  const fetchDone = useCallback(async (offset, searchParam) => {
    setLoadingDone(true);
    try {
      const { data } = await api.get("/tasks", {
        params: {
          status: "done",
          limit: DONE_PAGE_SIZE,
          offset,
          searchParam: searchParam || undefined,
          scope: teamView ? "team" : undefined,
        },
      });
      setDoneTasks(prev => (offset === 0 ? data : [...prev, ...data]));
      setDoneHasMore(data.length === DONE_PAGE_SIZE);
    } catch {
      toast.error("Erro ao carregar tarefas concluídas.");
    } finally {
      setLoadingDone(false);
    }
  }, [teamView]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // /todolist?task=ID (ex.: clique no lembrete do sininho) abre a tarefa
  const location = useLocation();
  const linkedTaskId = Number(new URLSearchParams(location.search).get("task")) || null;
  useEffect(() => {
    if (!linkedTaskId || loadingPending) return;
    const found = pendingTasks.find(t => t.id === linkedTaskId);
    if (found) setEditorTask(found);
    else toast.info("Essa tarefa não está mais pendente.");
    history.replace(location.pathname);
  }, [linkedTaskId, loadingPending, pendingTasks, history, location.pathname]);

  // concluídas só são buscadas sob demanda (aba aberta), paginadas no servidor
  useEffect(() => {
    if (tab === "done") fetchDone(0, debouncedSearch);
  }, [tab, debouncedSearch, fetchDone]);

  useEffect(() => {
    setSelectedIds([]);
  }, [tab]);

  const pendingCount = pendingTasks.length;
  const overdueCount = useMemo(() => pendingTasks.filter(isOverdue).length, [pendingTasks]);

  const visiblePending = useMemo(
    () =>
      pendingTasks
        .filter(t => !teamView || !assigneeFilter || t.userId === assigneeFilter)
        .filter(t => matchesSearch(t, debouncedSearch))
        .filter(t => (tab === "overdue" ? isOverdue(t) : true))
        .sort(SORTERS[sortBy]),
    [pendingTasks, debouncedSearch, tab, sortBy, teamView, assigneeFilter]
  );

  const groupedPending = useMemo(
    () =>
      GROUPS.map(g => ({
        ...g,
        tasks: visiblePending.filter(t => g.match(daysUntilDue(t))),
      })).filter(g => g.tasks.length > 0),
    [visiblePending]
  );

  const visibleTasks = tab === "done" ? doneTasks : visiblePending;

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", {
        text: newText.trim(),
        priority: newPriority,
        dueDate: newDueDate || null,
      });
      setPendingTasks(prev => [data, ...prev]);
      setNewText("");
      setNewPriority("medium");
      setNewDueDate("");
    } catch {
      toast.error("Erro ao criar tarefa.");
    } finally {
      setSaving(false);
    }
  };

  // PUT não devolve contato/oportunidade; preserva os que já vieram na listagem
  const applyUpdated = (original, data) => {
    const merged = { ...original, ...data };
    if (merged.completed) {
      setPendingTasks(prev => prev.filter(t => t.id !== merged.id));
      setDoneTasks(prev => [merged, ...prev.filter(t => t.id !== merged.id)]);
    } else {
      setDoneTasks(prev => prev.filter(t => t.id !== merged.id));
      setPendingTasks(prev =>
        prev.some(t => t.id === merged.id)
          ? prev.map(t => (t.id === merged.id ? merged : t))
          : [merged, ...prev]
      );
    }
  };

  const setPhase = (id, phase) =>
    setCompleting(prev => {
      const next = { ...prev };
      if (phase) next[id] = phase;
      else delete next[id];
      return next;
    });

  // novas conclusões entram no mesmo aviso e reiniciam o prazo do desfazer
  const pushUndo = tasks => {
    setUndoTasks(prev => [...prev.filter(t => !tasks.some(n => n.id === t.id)), ...tasks]);
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setUndoTasks([]), UNDO_MS);
  };

  const closeUndo = () => {
    clearTimeout(undoTimer.current);
    setUndoTasks([]);
  };

  // recolhe o card e só depois move a tarefa para "Concluídas"
  const animateOut = (original, data) => {
    const { id } = original;
    setPhase(id, "leaving");
    leaveTimers.current[id] = setTimeout(
      () => {
        delete leaveTimers.current[id];
        applyUpdated(original, data);
        setPhase(id, null);
      },
      reduceMotion ? 0 : COLLAPSE_MS
    );
    return { ...original, ...data };
  };

  const handleComplete = async task => {
    if (completing[task.id]) return;
    setPhase(task.id, "checked");
    try {
      if (navigator.vibrate) navigator.vibrate(15);
    } catch {}
    try {
      const [{ data }] = await Promise.all([
        api.put(`/tasks/${task.id}`, { completed: true }),
        new Promise(resolve => setTimeout(resolve, reduceMotion ? 0 : CHECK_PAUSE_MS)),
      ]);
      pushUndo([animateOut(task, data)]);
    } catch {
      setPhase(task.id, null);
      toast.error("Erro ao concluir tarefa.");
    }
  };

  const handleUndo = async () => {
    const tasks = undoTasks;
    closeUndo();
    const results = await Promise.allSettled(
      tasks.map(t => api.put(`/tasks/${t.id}`, { completed: false }))
    );
    results.forEach((r, i) => {
      if (r.status !== "fulfilled") return;
      const { id } = tasks[i];
      // se ainda estava recolhendo, nem chegou a sair de pendentes
      clearTimeout(leaveTimers.current[id]);
      delete leaveTimers.current[id];
      setPhase(id, null);
      restoredIds.current.add(id);
      applyUpdated(tasks[i], r.value.data);
    });
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) toast.error(`${failed} tarefa(s) não puderam ser reabertas.`);
  };

  // mantém o texto enquanto o aviso fecha (evita "0 tarefas concluídas")
  const undoCount = useRef(0);
  if (undoTasks.length) undoCount.current = undoTasks.length;

  const handleToggle = async task => {
    if (!task.completed) return handleComplete(task);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, {
        completed: !task.completed,
      });
      applyUpdated(task, data);
    } catch {
      toast.error("Erro ao atualizar tarefa.");
    }
  };

  const removeLocal = ids => {
    setPendingTasks(prev => prev.filter(t => !ids.includes(t.id)));
    setDoneTasks(prev => prev.filter(t => !ids.includes(t.id)));
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/tasks/${id}`);
      removeLocal([id]);
    } catch {
      toast.error("Erro ao excluir tarefa.");
    }
  };

  const handleEditStart = task => {
    setEditingId(task.id);
    setDraft({
      text: task.text,
      priority: task.priority || "medium",
      dueDate: taskDueInput(task),
    });
  };

  // valor lido fora do updater: no React 16 o evento é reciclado e e.target vira null
  const setDraftField = (field, value) => setDraft(d => ({ ...d, [field]: value }));

  const handleEditCancel = () => setEditingId(null);

  const handleEditSave = async task => {
    const text = draft.text.trim();
    if (!text) return;
    const changes = {};
    if (text !== task.text) changes.text = text;
    if (draft.priority !== task.priority) changes.priority = draft.priority;
    // mantém o horário da tarefa quando só a data muda
    if (draft.dueDate !== taskDueInput(task))
      changes.dueDate = buildDuePayload(draft.dueDate, taskDueTime(task));
    if (!Object.keys(changes).length) {
      setEditingId(null);
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, changes);
      applyUpdated(task, data);
      setEditingId(null);
    } catch {
      toast.error("Erro ao salvar tarefa.");
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleSelected = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const allVisibleSelected =
    visibleTasks.length > 0 && visibleTasks.every(t => selectedIds.includes(t.id));

  const handleSelectAll = () =>
    setSelectedIds(allVisibleSelected ? [] : visibleTasks.map(t => t.id));

  const handleBulkToggle = async () => {
    const targets = visibleTasks.filter(t => selectedIds.includes(t.id));
    const completed = tab !== "done";
    setBulkWorking(true);
    const results = await Promise.allSettled(
      targets.map(t => api.put(`/tasks/${t.id}`, { completed }))
    );
    const done = [];
    results.forEach((r, i) => {
      if (r.status !== "fulfilled") return;
      if (completed) done.push(animateOut(targets[i], r.value.data));
      else applyUpdated(targets[i], r.value.data);
    });
    if (done.length) pushUndo(done);
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) toast.error(`${failed} tarefa(s) não puderam ser atualizadas.`);
    setBulkWorking(false);
    exitSelectMode();
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkWorking(true);
    const results = await Promise.allSettled(ids.map(id => api.delete(`/tasks/${id}`)));
    removeLocal(ids.filter((_, i) => results[i].status === "fulfilled"));
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) toast.error(`${failed} tarefa(s) não puderam ser excluídas.`);
    setBulkWorking(false);
    exitSelectMode();
  };

  const handleConfirmDelete = () => {
    if (confirmDelete === "bulk") handleBulkDelete();
    else if (confirmDelete) handleDelete(confirmDelete.id);
  };

  const handlePostpone = async (task, dueDate) => {
    setPostpone(null);
    try {
      const time = taskDueTime(task);
      const { data } = await api.put(`/tasks/${task.id}`, { dueDate: buildDuePayload(dueDate, time) });
      applyUpdated(task, data);
      toast.success(
        `Adiada para ${dueDate.split("-").reverse().join("/")}${time ? ` às ${time}` : ""}.`
      );
    } catch {
      toast.error("Erro ao adiar tarefa.");
    }
  };

  // tarefa criada/editada pelo formulário completo
  const handleEditorSaved = saved => {
    const original = [...pendingTasks, ...doneTasks].find(t => t.id === saved.id);
    if (original) {
      applyUpdated(original, saved);
      // passou para outra pessoa: some de "Minhas"
      if (!teamView && saved.userId !== user?.id) removeLocal([saved.id]);
      return;
    }
    if (teamView || saved.userId === user?.id) {
      setPendingTasks(prev => [saved, ...prev]);
    }
  };

  const stop = e => e.stopPropagation();

  const renderTask = task => {
    const overdue = isOverdue(task);
    const days = daysUntilDue(task);
    const selected = selectedIds.includes(task.id);
    const label = dueLabel(task);
    const phase = completing[task.id];
    const showDone = task.completed || !!phase;
    const editing = editingId === task.id;

    return (
      <Collapse
        key={task.id}
        in={phase !== "leaving"}
        appear={restoredIds.current.has(task.id)}
        onEntered={() => restoredIds.current.delete(task.id)}
        timeout={reduceMotion ? 0 : COLLAPSE_MS}
      >
        <div className={classes.taskSlot}>
          <Paper
            className={[
              classes.taskCard,
              overdue && !phase && classes.taskCardOverdue,
              task.completed && !selectMode && classes.taskCardCompleted,
              phase && classes.taskCardCompleting,
              selected && classes.taskCardSelected,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={
              selectMode
                ? () => toggleSelected(task.id)
                : editing || phase
                ? undefined
                : () => setEditorTask(task)
            }
            style={editing ? undefined : { cursor: "pointer" }}
          >
            {selectMode ? (
              <Checkbox
                edge="start"
                size="small"
                color="primary"
                checked={selected}
                onClick={e => e.stopPropagation()}
                onChange={() => toggleSelected(task.id)}
                style={{ padding: 4 }}
              />
            ) : (
              <Checkbox
                edge="start"
                checked={showDone}
                onClick={stop}
                onChange={() => handleToggle(task)}
                className={phase ? classes.checkboxPop : undefined}
                classes={{ root: classes.checkboxRoot, checked: classes.checked }}
                size="small"
              />
            )}

            <div className={classes.taskBody}>
              {editing ? (
                <Input
                  className={classes.inlineInput}
                  value={draft.text}
                  onChange={e => setDraftField("text", e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleEditSave(task);
                    if (e.key === "Escape") handleEditCancel();
                  }}
                  autoFocus
                  fullWidth
                />
              ) : phase ? (
                <div className={classes.taskText}>
                  <span className={classes.taskTextStrike}>{task.text}</span>
                </div>
              ) : (
                <div
                  className={`${classes.taskText} ${task.completed ? classes.taskTextCompleted : ""}`}
                  onDoubleClick={
                    selectMode || task.completed
                      ? undefined
                      : e => {
                          stop(e);
                          handleEditStart(task);
                        }
                  }
                  title={selectMode || task.completed ? undefined : "Clique para abrir · duplo clique para renomear"}
                >
                  {task.text}
                </div>
              )}

              {editing && (
                <div className={classes.editRow} onClick={stop}>
                  <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                    <InputLabel>Prioridade</InputLabel>
                    <Select
                      value={draft.priority}
                      onChange={e => setDraftField("priority", e.target.value)}
                      label="Prioridade"
                    >
                      <MenuItem value="high">Alta</MenuItem>
                      <MenuItem value="medium">Média</MenuItem>
                      <MenuItem value="low">Baixa</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Prazo"
                    type="date"
                    variant="outlined"
                    size="small"
                    className={classes.editDate}
                    InputLabelProps={{ shrink: true }}
                    value={draft.dueDate}
                    onChange={e => setDraftField("dueDate", e.target.value)}
                    onKeyDown={e => e.key === "Escape" && handleEditCancel()}
                    InputProps={{
                      endAdornment: draft.dueDate ? (
                        <InputAdornment position="end">
                          <Tooltip title="Remover prazo">
                            <IconButton
                              size="small"
                              onClick={() => setDraft(d => ({ ...d, dueDate: "" }))}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  <div className={classes.editButtons}>
                    <Button size="small" className={classes.textButton} onClick={handleEditCancel} disabled={savingEdit}>
                      Cancelar
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disableElevation
                      className={classes.textButton}
                      onClick={() => handleEditSave(task)}
                      disabled={savingEdit || !draft.text.trim()}
                    >
                      Salvar
                    </Button>
                  </div>
                </div>
              )}

              {!editing && (
                <div className={classes.taskMeta}>
                  <span
                    className={classes.priorityBadge}
                    style={{
                      color: tone(PRIORITY_COLORS[task.priority] || "#9e9e9e"),
                      backgroundColor: alpha(PRIORITY_COLORS[task.priority] || "#9e9e9e", 0.14),
                    }}
                  >
                    <span
                      className={classes.priorityDot}
                      style={{ backgroundColor: PRIORITY_COLORS[task.priority] || "#9e9e9e" }}
                    />
                    {PRIORITY_LABELS[task.priority] || task.priority}
                  </span>
                  {label && (
                    <span
                      className={[
                        classes.dueDateText,
                        overdue && classes.dueDateOverdue,
                        !task.completed && days === 0 && classes.dueDateToday,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {label}
                    </span>
                  )}
                  {task.description && (
                    <Tooltip
                      title={
                        <span style={{ whiteSpace: "pre-wrap" }}>
                          {task.description.length > 300
                            ? `${task.description.slice(0, 300)}…`
                            : task.description}
                        </span>
                      }
                    >
                      <SubjectIcon
                        fontSize="small"
                        aria-label="Tem descrição"
                        style={{ color: theme.palette.text.secondary, fontSize: 16 }}
                      />
                    </Tooltip>
                  )}
                  {task.contact && (
                    <Tooltip title="Abrir contato">
                      <Chip
                        icon={<PersonIcon />}
                        label={task.contact.name}
                        size="small"
                        variant="outlined"
                        className={classes.linkChip}
                        clickable={!selectMode}
                        onClick={
                          selectMode
                            ? undefined
                            : e => {
                                stop(e);
                                history.push(`/contacts/${task.contact.id}/profile`);
                              }
                        }
                      />
                    </Tooltip>
                  )}
                  {task.opportunity && (
                    <Tooltip title="Abrir CRM">
                      <Chip
                        icon={<TrendingUpIcon />}
                        label={task.opportunity.title}
                        size="small"
                        variant="outlined"
                        className={classes.linkChip}
                        clickable={!selectMode}
                        onClick={
                          selectMode
                            ? undefined
                            : e => {
                                stop(e);
                                history.push("/crm");
                              }
                        }
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>

            {!selectMode && !editing && (
              <div className={`${classes.actions} todo-actions`} onClick={stop}>
                {!showDone && task.contact?.number && (
                  <Tooltip title="Conversar com o contato">
                    <IconButton size="small" onClick={() => setChatContact(task.contact)} aria-label="Conversar">
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!showDone && (
                  <Tooltip title="Adiar">
                    <IconButton
                      size="small"
                      onClick={e => setPostpone({ el: e.currentTarget, task })}
                      aria-label="Adiar"
                    >
                      <AlarmIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!showDone && (
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => setEditorTask(task)} aria-label="Editar">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Excluir">
                  <IconButton size="small" onClick={() => setConfirmDelete(task)} aria-label="Excluir">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
            {teamView && task.user?.name && (
              <Tooltip title={`Responsável: ${task.user.name}`}>
                <span className={classes.owner}>
                  {task.user.name
                    .split(/\s+/)
                    .slice(0, 2)
                    .map(w => w[0])
                    .join("")
                    .toUpperCase()}
                </span>
              </Tooltip>
            )}
          </Paper>
        </div>
      </Collapse>
    );
  };

  const renderEmpty = () => {
    let msg = "Nenhuma tarefa encontrada.";
    if (debouncedSearch) msg = `Nenhuma tarefa encontrada para "${debouncedSearch}".`;
    else if (tab === "pending") msg = "Tudo em dia! Nenhuma tarefa pendente.";
    else if (tab === "overdue") msg = "Nenhuma tarefa atrasada.";
    else if (tab === "done") msg = "Nenhuma tarefa concluída ainda.";
    return (
      <div className={classes.emptyState}>
        <Typography>{msg}</Typography>
      </div>
    );
  };

  const renderList = () => {
    if (tab === "done") {
      if (loadingDone && doneTasks.length === 0) {
        return (
          <div style={{ textAlign: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        );
      }
      if (doneTasks.length === 0) return renderEmpty();
      return (
        <>
          {doneTasks.map(renderTask)}
          {doneHasMore && (
            <div className={classes.loadMore}>
              <Button
                variant="outlined"
                size="small"
                disabled={loadingDone}
                onClick={() => fetchDone(doneTasks.length, debouncedSearch)}
                startIcon={loadingDone ? <CircularProgress size={14} /> : null}
              >
                Carregar mais
              </Button>
            </div>
          )}
        </>
      );
    }

    if (loadingPending) {
      return (
        <div style={{ textAlign: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      );
    }
    if (visiblePending.length === 0) return renderEmpty();
    if (tab === "overdue") return visiblePending.map(renderTask);

    return groupedPending.map(group => {
      const isCollapsed = !!collapsed[group.key];
      return (
        <div key={group.key}>
          <button
            type="button"
            className={`${classes.groupHeader} ${group.key === "overdue" ? classes.groupHeaderOverdue : ""}`}
            onClick={() => setCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            {group.label}
            <span className={classes.groupCount}>{group.tasks.length}</span>
          </button>
          {!isCollapsed && group.tasks.map(renderTask)}
        </div>
      );
    });
  };

  return (
    <div className={classes.root}>
      <div className={`${classes.header} ${classes.headerRow}`}>
        <div>
          <div className={classes.titleRow}>
            <Typography component="h1" className={classes.title}>
              {teamView ? "Tarefas da equipe" : "Minhas tarefas"}
            </Typography>
            {canSeeTeam && (
              <div className={classes.scopeToggle} role="group" aria-label="De quem">
                {[
                  ["mine", "Minhas"],
                  ["team", "Equipe"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={scope === key}
                    className={`${classes.scopeButton} ${scope === key ? classes.scopeButtonActive : ""}`}
                    onClick={() => {
                      setScope(key);
                      setAssigneeFilter("");
                      setLoadingPending(true);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Typography className={classes.summary}>
            {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
            {overdueCount > 0 && (
              <span style={{ color: tone("#e53935"), fontWeight: 600 }}>
                {" "}· {overdueCount} atrasada{overdueCount !== 1 ? "s" : ""}
              </span>
            )}
            {teamView ? " · todas as pessoas da empresa" : ""}
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          className={classes.newTaskButton}
          startIcon={<AddIcon />}
          onClick={() => setCreatorOpen(true)}
        >
          Nova tarefa
        </Button>
      </div>

      <Paper className={classes.form} elevation={0}>
        <TextField
          placeholder="O que precisa ser feito?"
          variant="outlined"
          size="small"
          fullWidth
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyPress={e => e.key === "Enter" && handleAdd()}
          inputProps={{ "aria-label": "Nova tarefa" }}
        />
        <div className={classes.formRow}>
          <div className={classes.priorityPicker} role="radiogroup" aria-label="Prioridade">
            {["high", "medium", "low"].map(value => {
              const active = newPriority === value;
              const color = PRIORITY_COLORS[value];
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={classes.priorityOption}
                  onClick={() => setNewPriority(value)}
                  style={
                    active
                      ? { borderColor: color, backgroundColor: alpha(color, 0.14), color: tone(color) }
                      : undefined
                  }
                >
                  <span className={classes.priorityDot} style={{ backgroundColor: color }} />
                  {PRIORITY_LABELS[value]}
                </button>
              );
            })}
          </div>

          <TextField
            type="date"
            variant="outlined"
            size="small"
            className={classes.formDate}
            value={newDueDate}
            onChange={e => setNewDueDate(e.target.value)}
            inputProps={{ "aria-label": "Prazo" }}
            helperText={newDueDate ? undefined : "Prazo (opcional)"}
            FormHelperTextProps={{ style: { margin: "2px 4px 0" } }}
          />

          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.addButton}
            onClick={handleAdd}
            disabled={saving || !newText.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            Adicionar tarefa
          </Button>
        </div>
      </Paper>

      <div className={classes.toolbar}>
        <div className={classes.toolbarRow}>
          <TextField
            className={classes.search}
            placeholder="Buscar tarefa, contato ou oportunidade"
            variant="outlined"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {teamView && tab !== "done" && (
            <TextField
              select
              variant="outlined"
              size="small"
              className={classes.sortSelect}
              value={assigneeFilter}
              onChange={e => setAssigneeFilter(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ "aria-label": "Responsável" }}
            >
              <MenuItem value="">Todos os responsáveis</MenuItem>
              {Array.from(
                new Map(pendingTasks.filter(t => t.user).map(t => [t.user.id, t.user])).values()
              ).map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.id === user?.id ? `${u.name} (você)` : u.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {tab !== "done" && (
            <TextField
              select
              variant="outlined"
              size="small"
              className={classes.sortSelect}
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              inputProps={{ "aria-label": "Ordenar por" }}
            >
              <MenuItem value="priority">Ordenar: prioridade</MenuItem>
              <MenuItem value="dueDate">Ordenar: prazo</MenuItem>
              <MenuItem value="recent">Ordenar: mais recentes</MenuItem>
            </TextField>
          )}
        </div>

        <div className={classes.tabs}>
          {TABS.map(t => {
            let label = t.label;
            if (t.key === "pending") label = `${t.label} (${pendingCount})`;
            if (t.key === "overdue" && overdueCount > 0) label = `${t.label} (${overdueCount})`;
            return (
              <Chip
                key={t.key}
                label={label}
                clickable
                className={classes.tabChip}
                onClick={() => setTab(t.key)}
                color={tab === t.key ? "primary" : "default"}
                variant={tab === t.key ? "default" : "outlined"}
                aria-pressed={tab === t.key}
              />
            );
          })}
          <div style={{ flex: 1 }} />
          {!selectMode && visibleTasks.length > 0 && (
            <Button size="small" className={classes.textButton} onClick={() => setSelectMode(true)}>
              Selecionar
            </Button>
          )}
        </div>

        {selectMode && (
          <div className={classes.bulkBar}>
            <Checkbox
              size="small"
              color="primary"
              checked={allVisibleSelected}
              indeterminate={selectedIds.length > 0 && !allVisibleSelected}
              onChange={handleSelectAll}
              style={{ padding: 4 }}
            />
            <span className={classes.bulkCount}>
              {selectedIds.length} selecionada{selectedIds.length !== 1 ? "s" : ""}
            </span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!selectedIds.length || bulkWorking}
              onClick={handleBulkToggle}
            >
              {tab === "done" ? "Reabrir" : "Concluir"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedIds.length || bulkWorking}
              onClick={() => setConfirmDelete("bulk")}
            >
              Excluir
            </Button>
            <Button size="small" onClick={exitSelectMode} disabled={bulkWorking}>
              Cancelar
            </Button>
          </div>
        )}
      </div>

      {renderList()}

      <TaskEditor
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        onSaved={saved => {
          handleEditorSaved(saved);
          setNewText("");
        }}
        initialText={newText}
      />
      <TaskEditor
        open={Boolean(editorTask)}
        task={editorTask}
        onClose={() => setEditorTask(null)}
        onSaved={handleEditorSaved}
        onDeleted={task => {
          setEditorTask(null);
          setConfirmDelete(task);
        }}
        onToggleComplete={task => {
          setEditorTask(null);
          handleToggle(task);
        }}
      />
      <Menu
        anchorEl={postpone?.el}
        open={Boolean(postpone)}
        onClose={() => setPostpone(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {postpone &&
          postponeOptions(postpone.task).map(option => (
            <MenuItem key={option.key} onClick={() => handlePostpone(postpone.task, option.value)}>
              <span style={{ flex: 1 }}>{option.label}</span>
              <span style={{ marginLeft: 16, fontSize: 12, opacity: 0.7 }}>
                {option.value.split("-").reverse().slice(0, 2).join("/")}
              </span>
            </MenuItem>
          ))}
      </Menu>
      {chatContact && (
        <NewTicketModal
          modalOpen={Boolean(chatContact)}
          initialContact={chatContact}
          onClose={ticket => {
            setChatContact(null);
            if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
          }}
        />
      )}

      <ConfirmationModal
        title={confirmDelete === "bulk" ? "Excluir tarefas" : "Excluir tarefa"}
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
      >
        {confirmDelete === "bulk"
          ? `Excluir ${selectedIds.length} tarefa(s) selecionada(s)? Essa ação não pode ser desfeita.`
          : "Excluir esta tarefa? Essa ação não pode ser desfeita."}
      </ConfirmationModal>

      <Snackbar
        open={undoTasks.length > 0}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        className={classes.undoSnackbar}
        message={
          undoCount.current === 1 ? "Tarefa concluída" : `${undoCount.current} tarefas concluídas`
        }
        action={
          <>
            <Button size="small" onClick={handleUndo} className={classes.undoButton}>
              Desfazer
            </Button>
            <IconButton size="small" color="inherit" aria-label="Fechar" onClick={closeUndo}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </div>
  );
};

export default ToDoList;
